import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, MapPin } from "lucide-react";
import { CATEGORIES } from "../lib/constants";
import { StatusBadge } from "../components/StatusBadge";

export default function Dashboard() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/my/listings");
      setItems(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this listing?")) return;
    try {
      await api.delete(`/listings/${id}`);
      toast.success("Deleted");
      setItems((p) => p.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6 sm:py-10" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="label-caps">My workspace</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-primary tracking-tight mt-1">
            Welcome, {user?.name?.split(" ")[0] || "there"}
          </h1>
          <p className="text-muted-foreground mt-1">Manage your renewable energy listings.</p>
        </div>
        <Link to="/dashboard/new">
          <Button data-testid="new-listing-btn" className="bg-primary hover:bg-primary/90 gap-2">
            <Plus className="h-4 w-4" /> New listing
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="py-20 text-center text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-20 text-center">
            <div className="font-display text-2xl text-primary">No listings yet.</div>
            <p className="text-muted-foreground mt-2 mb-6">Start by posting your first opportunity.</p>
            <Link to="/dashboard/new">
              <Button className="bg-primary hover:bg-primary/90">Post a listing</Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4" data-testid="my-listings">
          {items.map((it) => {
            const img = it.image_paths?.[0];
            return (
              <Card key={it.id} className="hover:shadow-sm transition-shadow">
                <CardContent className="p-3 sm:p-4 flex gap-3 sm:gap-4">
                  <div className="w-20 h-20 sm:w-32 sm:h-24 rounded-lg bg-secondary overflow-hidden shrink-0">
                    {img ? (
                      <img src={fileUrl(img)} className="w-full h-full object-cover" alt="" />
                    ) : (
                      <div className="w-full h-full grid place-items-center text-muted-foreground text-xs">
                        No image
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex gap-2 flex-wrap">
                      <Badge className="bg-primary/10 text-primary border-0">
                        {CATEGORIES.find((c) => c.value === it.category)?.label}
                      </Badge>
                      {it.technology && <Badge variant="outline" className="capitalize">{it.technology}</Badge>}
                      {it.listing_type === "buy" && <Badge className="bg-accent/90 text-accent-foreground border-0">Requirement</Badge>}
                      <StatusBadge status={it.status} />
                    </div>
                    <Link to={`/listing/${it.id}`}>
                      <h3 className="font-display text-base sm:text-xl font-semibold mt-2 hover:text-primary line-clamp-2">{it.title}</h3>
                    </Link>
                    <div className="text-sm text-muted-foreground mt-1 flex items-center gap-3">
                      <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {it.state}</span>
                      {it.capacity_mw && <span>· {it.capacity_mw} MW</span>}
                      {it.land_area_acres && <span>· {it.land_area_acres} acres</span>}
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={`delete-${it.id}`}
                      onClick={() => del(it.id)}
                      className="text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
